import { useState, useEffect, useMemo } from "react";
import { GoBell, GoSearch, GoChevronRight, GoChevronDown, GoCheck, GoHome } from "react-icons/go";
import { RiMenu2Fill } from "react-icons/ri";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LuUserRound } from "react-icons/lu";
import { IoHelpCircleOutline, IoLogOutOutline } from "react-icons/io5";
import { useAuth } from "../../context/auth";
import { AWS_S3_URL } from "../../utils/constants";
import { useTranslation } from "react-i18next";
import { searchItems, categoryMeta } from "../../utils/searchItems";
import { BsLayoutSidebarInsetReverse } from "react-icons/bs";
import BranchSwitcher from "./BranchSwitcher";
import { post } from "../../utils/helpers/api";
import './Header.css';

function Header({ toggleSidebar, isMain = false, sidebarCollapsed = false, toggleSidebarCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { handleLogout, user, can } = useAuth();

  const lang = i18n.language;
  const getLabel = (item) => {
    if (lang === 'fr' && item.labelFr) return item.labelFr;
    if (lang === 'ar' && item.labelAr) return item.labelAr;
    return item.label;
  };
  const getCategoryLabel = (category) => {
    const meta = categoryMeta[category];
    if (!meta) return category;
    if (lang === 'fr' && meta.labelFr) return meta.labelFr;
    if (lang === 'ar' && meta.labelAr) return meta.labelAr;
    return category;
  };

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    return searchItems.filter(item =>
      item.label.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q) ||
      item.keywords.some(k => k.includes(q))
    ).slice(0, 8);
  }, [searchQuery]);

  const apps = [
    { key: "crm", title: t('nav.crm'), icon: require('../../assets/images/icons/crm.png'), link: "/crm/home", permission: 'crm.home' },
    { key: "sales", title: t('nav.sales'), icon: require('../../assets/images/icons/sales.png'), link: "/sales/home", permission: 'sales.home' },
    { key: "purchases", title: t('nav.purchases'), icon: require('../../assets/images/icons/purchases.png'), link: "/purchases/home", permission: 'purchases.home' },
    { key: "inventory", title: t('nav.inventory'), icon: require('../../assets/images/icons/inventory.png'), link: "/inventory/home", permission: 'inventory.home' },
    { key: "production", title: t('nav.production'), icon: require('../../assets/images/icons/production.png'), link: "/production/home", permission: 'production.home' },
    { key: "hr", title: t('nav.hr'), icon: require('../../assets/images/icons/hr.png'), link: "/hr/home", permission: 'hr.home' },
    { key: "accounting", title: t('nav.accounting'), icon: require('../../assets/images/icons/accounting.png'), link: "/accounting/home", permission: 'accounting.home' },
    { key: "settings", title: t('nav.settings'), icon: require('../../assets/images/icons/settings.png'), link: "/settings/home" },
  ];

  const moduleKey = location.pathname.split("/")[1];
  const currentModule = apps.find((app) => app.key === moduleKey);

  useEffect(() => {
    const closeAll = () => {
      setNotificationsOpen(false);
      setProfileOpen(false);
      setSearchOpen(false);
      setBranchOpen(false);
      setModuleOpen(false);
    };
    document.addEventListener("click", closeAll);
    return () => document.removeEventListener("click", closeAll);
  }, []);

  const logout = async () => {
    const data = await post('/logout');

    await handleLogout();
    navigate("/");
  };

  const handleResultClick = (path) => {
    navigate(path);
    setSearchQuery("");
    setSearchOpen(false);
    setActiveIndex(-1);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setActiveIndex(-1);
    setSearchOpen(value.trim().length > 0);
  };

  const handleSearchKeyDown = (e) => {
    if (!searchOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && activeIndex >= 0 && searchResults[activeIndex]) {
      handleResultClick(searchResults[activeIndex].path);
    } else if (e.key === "Escape") {
      setSearchOpen(false);
      setActiveIndex(-1);
    }
  };

  return(
    <header className="header">
      <div className="header-left">
        {!isMain &&
          <button className="menu-toggle" id="menuToggle" onClick={toggleSidebar}>
            <RiMenu2Fill />
          </button>
        }
        {!isMain && currentModule ? (
          <div className="module-switcher" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={`module-switcher-btn ${moduleOpen ? "open" : ""}`}
              id="moduleSwitcherBtn"
              aria-haspopup="true"
              aria-expanded={moduleOpen}
              onClick={() => {
                setModuleOpen((v) => !v);
                setNotificationsOpen(false);
                setProfileOpen(false);
                setBranchOpen(false);
              }}
            >
              <img
                src={require('../../assets/images/brand.svg').default}
                className="module-switcher-brand"
                alt={t('header.logo')}
              />
              <span className="module-switcher-name">{currentModule.title}</span>
              <GoChevronDown className="module-switcher-caret" />
            </button>

            <div className={`dropdown-menu-custom module-dropdown ${moduleOpen ? "show" : ""}`} id="moduleDropdown">
              <div className="dropdown-header">{t('apps.appsTitle')}</div>
              <div className="module-list">
                {apps.filter(app => !app.permission || can(app.permission)).map((app) => (
                  <Link
                    key={app.key}
                    to={app.link}
                    className={`module-item ${app.key === currentModule.key ? "active" : ""}`}
                    onClick={() => setModuleOpen(false)}
                  >
                    <img src={app.icon} className="module-item-img" alt="" />
                    <span className="module-item-label">{app.title}</span>
                    {app.key === currentModule.key && <GoCheck className="module-item-check" />}
                  </Link>
                ))}
              </div>
              <div className="dropdown-divider"></div>
              <Link to="/home" className="dropdown-item-custom" onClick={() => setModuleOpen(false)}>
                <GoHome />
                <span>{t('nav.home')}</span>
              </Link>
            </div>
          </div>
        ) : (
          <Link className="logo" to="/home">
            <img
              src={require('../../assets/images/logo.svg').default}
              width={140}
              alt={t('header.logo')}
            />
          </Link>
        )}

        {!isMain &&
          <button
            type="button"
            className={`header-sidebar-toggle-btn ${sidebarCollapsed ? "collapsed" : ""}`}
            onClick={toggleSidebarCollapse}
            title={sidebarCollapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
            aria-label={sidebarCollapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
          >
            <BsLayoutSidebarInsetReverse size={18} />
          </button>
        }
      </div>

      <div className="header-search-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="header-search">
          <GoSearch className="header-search-icon" />
          <input
            type="search"
            className="header-search-input"
            placeholder={t('header.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => { if (searchQuery.trim()) setSearchOpen(true); }}
            onKeyDown={handleSearchKeyDown}
          />
        </div>

        {searchOpen && (
          <div className="search-dropdown">
            {searchResults.length === 0 ? (
              <div className="search-no-results">
                {t('header.noResultsFor')} "<strong>{searchQuery}</strong>"
              </div>
            ) : (
              <>
                <div className="search-results-list">
                  {searchResults.map((item, index) => (
                    <button
                      key={item.path}
                      className={`search-result-item ${index === activeIndex ? "active" : ""}`}
                      onClick={() => handleResultClick(item.path)}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <span
                        className="search-category-badge"
                        style={{
                          color: categoryMeta[item.category]?.color,
                          background: categoryMeta[item.category]?.bg,
                        }}
                      >
                        {getCategoryLabel(item.category)}
                      </span>
                      <span className="search-result-label">{getLabel(item)}</span>
                      <GoChevronRight className="search-result-arrow" />
                    </button>
                  ))}
                </div>
                <div className="search-footer">
                  <kbd>↑</kbd> <kbd>↓</kbd> to navigate · <kbd>↵</kbd> to open · <kbd>Esc</kbd> to close
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="header-right">
        <BranchSwitcher
          open={branchOpen}
          setOpen={setBranchOpen}
          onOpen={() => {
            setNotificationsOpen(false);
            setProfileOpen(false);
            setModuleOpen(false);
          }}
        />

        <div className="header-dropdown">
          <div style={{ position: "relative" }}>
            <button
              className="header-icon"
              id="notificationsBtn"
              onClick={(e) => {
                e.stopPropagation();
                setNotificationsOpen(!notificationsOpen);
                setProfileOpen(false);
                setModuleOpen(false);
              }}
            >
              <GoBell />
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>

            <div className={`dropdown-menu-custom ${notificationsOpen ? "show" : ""}`} id="notificationsDropdown">
              <div className="dropdown-header">{t('header.notifications')}</div>
              {notifications?.length > 0 ? notifications?.map((n) => (
                <div key={n.id} className="notification-item">
                  <div className="notification-title">{n.title}</div>
                  <div className="notification-text">{n.text}</div>
                  <div className="notification-time">{n.time}</div>
                </div>
              )) :
                <div className="notification-item border-bottom-0 mt-1">
                  <div className="text-center text-muted">
                    {t('header.noNotifications')}
                  </div>
                </div>
              }
              <div className="dropdown-divider"></div>
              <button className="dropdown-item-custom" onClick={() => setNotificationsOpen(false)}>
                <span>{t('header.viewAllNotifications')}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="header-dropdown">
          <div style={{ position: "relative" }}>
            {user?.profile_picture_path ?
              <img
                src={`${AWS_S3_URL}${user.profile_picture_path}`}
                alt={t('common.avatar')}
                className="user-avatar"
                width={'100%'}
                height={'100%'}
                id="profileBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileOpen((v) => !v);
                  setNotificationsOpen(false);
                  setModuleOpen(false);
                }}
              />
            :
              <button
                className="user-avatar"
                id="profileBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileOpen((v) => !v);
                  setNotificationsOpen(false);
                  setModuleOpen(false);
                }}
              >
                { user?.firstname?.[0] || "" }
                { user?.lastname?.[0] || "" }
              </button>
            }

            <div className={`dropdown-menu-custom ${profileOpen ? "show" : ""}`} id="profileDropdown">
              <div className="dropdown-header">{ user?.firstname + " " + user?.lastname }</div>
              <button className="dropdown-item-custom" onClick={() => navigate("/profile")}>
                <LuUserRound />
                <span>{t('header.myProfile')}</span>
              </button>
              <button className="dropdown-item-custom" onClick={() => setProfileOpen(false)}>
                <IoHelpCircleOutline />
                <span>{t('header.helpCenter')}</span>
              </button>
              <div className="dropdown-divider"></div>
              <button className="dropdown-item-custom" onClick={() => logout()}>
                <IoLogOutOutline />
                <span>{t('header.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
